import React, { Suspense, useState, useEffect, useTransition } from 'react';
import axios from 'axios';
import { HashRouter, Routes, Route } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// i18n 관련 추가
import { I18nextProvider } from 'react-i18next';
import i18n, { initializeI18n } from '@utils/i18n';

// MUI 관련 추가
import CssBaseline from "@mui/material/CssBaseline";

// 기존 컴포넌트
import Common from './Common';
import RemoteAuth from './app/containers/_commons/RemoteAuth';
import Login from './app/containers/_commons/Login';
import { LeftSidebar } from "./app/components/_commons";
import { Header } from "./app/components/_commons";
import LoadingComponent from './app/components/_commons/LoadingComponent';
import { setLang, setLangList } from './app/modules/ui';

import './resources/assets/styles/main.scss';

function App() {
  const dispatch = useDispatch();
  const accessToken = localStorage.getItem('accessToken');
  const sidebarCollapsed = useSelector((state) => state.ui.sidebarCollapsed);
  const theme = useSelector((state) => state.ui.theme);
  const userData = useSelector((state) => state.organization.userData);
  const organizationData = useSelector((state) => state.organization.organizationData);
  const lang = useSelector((state) => state.ui.lang);

  const [orgList, setOrgList] = useState([]);

  useEffect(() => {
    console.log('init Ap.tsx')
  }, [])

  // i18n 초기화 및 언어 설정
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        // Determine currentLanguageCode based on lang state or browser language
        let currentLanguageCode;
        if (lang && lang.locale) {
          currentLanguageCode = lang.locale;
        } else {
          const browserLang = navigator.language || navigator.languages[0] || 'ko-KR';
          currentLanguageCode = typeof i18n.language === 'string' ? i18n.language : browserLang;
        }

        const response = await fetch('https://lgpk.coursemos.io/lst/' + currentLanguageCode);
        const data = await response.json();
        const sortedData = data.sort((a, b) => a.localizedName.localeCompare(b.localizedName));
        dispatch(setLangList(sortedData));

        if (lang && lang.locale) {
          await initializeI18n(lang, dispatch);
        } else {
          const currentLang = sortedData.find(el => el.locale === currentLanguageCode);
          if (currentLang) {
            dispatch(setLang(currentLang));
            await initializeI18n(currentLang, dispatch);
          } else {
            // Fallback to Korean if current language is not found
            const fallbackLanguage = {
              locale: "ko-KR",
              endonym: "한국어 (대한민국)",
              localizedName: "한국어 (대한민국)"
            };
            dispatch(setLang(fallbackLanguage));
            await initializeI18n(fallbackLanguage, dispatch);
          }
        }
      } catch (error) {
        console.error("Failed to initialize language:", error);
        // Fallback to Korean on error
        const fallbackLanguage = {
          locale: "ko-KR",
          endonym: "한국어 (대한민국)",
          localizedName: "한국어 (대한민국)"
        };
        dispatch(setLang(fallbackLanguage));
        await initializeI18n(fallbackLanguage, dispatch);
      }
    };

    initializeLanguage();
  }, [dispatch, lang]);

  useEffect(() => {
    console.log("Current theme:", theme);
    // body 요소에 theme 클래스 추가
    const element = document.body;
    if (element) {
      // 기존 theme- 클래스들 제거
      element.className = element.className.replace(/theme-\w+/g, '');
      // 새로운 theme 클래스 추가
      element.classList.add(`theme-${theme}`);
    }

    // 기존 테마 CSS 제거
    const existingThemeCSS = document.querySelector('link[data-theme-css]');
    if (existingThemeCSS) {
      existingThemeCSS.remove();
    }

    // 새로운 테마 CSS 로드
    const orglink = document.createElement('link');
    orglink.rel = 'stylesheet';
    orglink.href = `https://cdn.coursemos.io/css/${organizationData.organization_code}-dev.css`;
    orglink.setAttribute('data-organization-css', theme);
    document.head.appendChild(orglink);

    // 새로운 테마 CSS 로드
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://cdn.coursemos.io/css/${theme}-dev.css`;
    link.setAttribute('data-theme-css', theme);
    document.head.appendChild(link);

  }, [theme]);

  // 조직 리스트 가져오기
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const data = JSON.stringify({ "listToken": "En7lfpF74U8QA39uo8F0PkzeLbTtti" });
        const config = {
          method: 'post',
          url: `${API_URL}/${API_VERSION}/organization/getOrganization`,
          headers: { 'Content-Type': 'application/json' },
          data: data
        };

        const response = await axios(config);
        if (response.data.code === 100) {
          const tmpSelectOrganizationList = response.data.organizationList.map((val) => ({
            value: val.organization_code,
            label: val.organization_name,
            open_token: val.open_key,
          }));
          setOrgList(tmpSelectOrganizationList);
        }
      } catch (error) {
        console.error("Failed to fetch organizations:", error);
      }
    };

    fetchOrganizations();
  }, []);

  // 사이드바 애니메이션 조정
  useEffect(() => {
    if (sidebarCollapsed) {
      requestAnimationFrame(() => {
        const elements = document.querySelectorAll('.ps-submenu-content');
        elements.forEach(element => {
          const transform = window.getComputedStyle(element).transform;
          if (transform !== 'none') {
            const matrix = new DOMMatrix(transform);
            const translateX = matrix.m41;
            const translateY = matrix.m42;
            const newTranslateY = translateY - 15 + (element.offsetHeight * 0.5);
            element.style.transform = `translate(${translateX}px, ${newTranslateY}px)`;
          }
        });
      });
    }
  }, [sidebarCollapsed]);

  return (
    <Suspense fallback={<LoadingComponent />}>
      <I18nextProvider i18n={i18n}>
        <CssBaseline />
        <HashRouter>
          <ToastContainer style={{ width: "340px" }} />
          <div className='container-app'>
            <Routes>
              <Route path="/remoteAuth/:key" element={<RemoteAuth orgList={orgList} />} />
              {accessToken && Object.keys(userData).length > 0 ?
                (
                  <Route
                    path="*"
                    element={
                      <>
                        <LeftSidebar collapsed={sidebarCollapsed} toggled={true} />
                        <main className={`mod-${sidebarCollapsed ? 'hide' : 'open'}`}>
                          <Header />
                          <Common />
                        </main>
                      </>
                    }
                  />
                ) : (
                  <Route path="*" element={<Login orgList={orgList} />} />
                )}
            </Routes>
          </div>
        </HashRouter>
      </I18nextProvider>
    </Suspense>
  );
}

export default App;
